import { useEffect, useMemo, useState } from "react";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { BadgeCheck, CalendarDays, Loader2, MailPlus, Pencil, Search, Stethoscope, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";

const CLINICIAN_FIELDS = [
  ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"],
  ["phone", "Phone"], ["profession", "Profession"], ["specialty", "Specialty"],
  ["suburb", "Suburb"], ["state", "State"], ["postcode", "Postcode"], ["ahpra_number", "AHPRA number"],
];

const GP_FIELDS = [
  ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"],
  ["clinic_name", "Clinic name"], ["suburb", "Suburb"],
];

const fmtDate = (value) => value ? new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

const inDateRange = (value, range) => {
  if (range === "all") return true;
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  if (range === "today") return date.toDateString() === now.toDateString();
  const days = Number(range);
  return date >= new Date(now.getTime() - days * 86400000);
};

const prepareRows = (rows, query, dateRange, sort, accountView = "regular") => {
  const needle = query.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    const matchesAccountType = accountView === "test" ? Boolean(row.is_test) : !row.is_test;
    const matchesText = !needle || Object.values(row).some((value) =>
      typeof value === "string" && value.toLowerCase().includes(needle)
    );
    return matchesAccountType && matchesText && inDateRange(row.created_at, dateRange);
  });
  return [...filtered].sort((a, b) => {
    if (sort === "oldest") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    if (sort === "newest") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sort === "email") return (a.email || "").localeCompare(b.email || "");
    return ((a.last_name || "") + (a.first_name || "")).localeCompare((b.last_name || "") + (b.first_name || ""));
  });
};

const EntryTools = ({ searchOpen, setSearchOpen, query, setQuery, dateRange, setDateRange, sort, setSort, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Button type="button" variant="outline" size="icon" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search entries" title="Search names, email and details">
      {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
    </Button>
    {searchOpen && (
      <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or details…" className="w-64" data-testid="admin-entry-search" />
    )}
    <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Filter by signup date">
      <option value="all">Any date</option>
      <option value="today">Today</option>
      <option value="7">Last 7 days</option>
      <option value="30">Last 30 days</option>
      <option value="90">Last 90 days</option>
    </select>
    <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Sort entries">
      <option value="newest">Newest signup</option>
      <option value="oldest">Oldest signup</option>
      <option value="name">Name A–Z</option>
      <option value="email">Email A–Z</option>
    </select>
    {children}
  </div>
);

export const AdminUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("newest");
  const [profession, setProfession] = useState("all");
  const [accountView, setAccountView] = useState("regular");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ account_type: "psychologist", email: "", first_name: "", clinic_name: "", profession: "Psychologist" });
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    const res = await authedFetch("/admin/clinicians");
    setRows(res.ok ? await res.json() : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => prepareRows(
    profession === "all" ? rows : rows.filter((row) => row.profession === profession),
    query, dateRange, sort, accountView
  ), [rows, query, dateRange, sort, profession, accountView]);

  const openEdit = (row) => {
    setEditing(row);
    setForm(Object.fromEntries([...CLINICIAN_FIELDS, ["is_active", "Directory listing"], ["is_test", "Test account"]].map(([key]) => [key, row[key] ?? ""])));
  };

  const save = async () => {
    setSaving(true);
    const res = await authedFetch("/admin/clinicians/" + editing.id, { method: "PATCH", body: JSON.stringify(form) });
    setSaving(false);
    if (!res.ok) { toast.error("Could not update clinician."); return; }
    toast.success("Clinician updated.");
    setEditing(null);
    load();
  };

  const sendApprovalEmail = async () => {
    if (!editing) return;
    if (!window.confirm(`Send the AHPRA confirmation email to ${editing.email}?`)) return;
    setSendingApproval(true);
    const res = await authedFetch("/admin/clinicians/" + editing.id + "/verify", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    setSendingApproval(false);
    if (!res.ok) { toast.error(data.detail || "Could not send the AHPRA confirmation email."); return; }
    if (data.approval_email_sent) toast.success("AHPRA confirmation email sent.");
    else if (data.approval_email_already_sent) toast.info("The AHPRA confirmation email was already sent.");
    else toast.error("The clinician is approved, but the email was not sent. Please check Resend.");
    setEditing(null);
    load();
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${editing.first_name} ${editing.last_name}, their sign-in account, profile and related individual subscription? This cannot be undone.`)) return;
    const res = await authedFetch("/admin/users/clinician/" + editing.id, { method: "DELETE" });
    if (!res.ok) return toast.error("Could not delete this clinician account.");
    toast.success("Clinician account permanently deleted.");
    setEditing(null); load();
  };

  const sendInvite = async () => {
    setInviting(true);
    const res = await authedFetch("/admin/users/invite", { method: "POST", body: JSON.stringify(invite) });
    setInviting(false);
    if (!res.ok) return toast.error((await res.json().catch(() => ({}))).detail || "Could not send invitation.");
    toast.success("Invitation sent through Resend.");
    setInviteOpen(false);
    setInvite({ account_type: "psychologist", email: "", first_name: "", clinic_name: "", profession: "Psychologist" });
  };

  const professions = [...new Set(rows.map((row) => row.profession).filter(Boolean))].sort();

  return (
    <section data-testid="admin-users-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-slate-900"><Stethoscope className="h-5 w-5 text-primary" /> Clinician users</h2>
          <p className="mt-1 text-sm text-slate-500">Psychologists and psychiatrists with account details, signup dates and directory controls.</p>
        </div>
        <div className="flex flex-wrap gap-2"><EntryTools {...{ searchOpen, setSearchOpen, query, setQuery, dateRange, setDateRange, sort, setSort }}>
          <select value={profession} onChange={(e) => setProfession(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" aria-label="Filter profession">
            <option value="all">All professions</option>
            {professions.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select value={accountView} onChange={(e) => setAccountView(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium" aria-label="Show regular or test clinician accounts">
            <option value="regular">Regular users</option>
            <option value="test">Test accounts</option>
          </select>
        </EntryTools><Button onClick={() => setInviteOpen(true)}><MailPlus className="mr-2 h-4 w-4" /> Invite clinician or clinic</Button></div>
      </div>
      <UserTable
        loading={loading}
        rows={visible}
        columns={[
          ["Clinician", (row) => <div className="flex items-center gap-3">{row.photo_url ? <img src={row.photo_url} alt="" className="h-10 w-10 shrink-0 rounded-md border border-slate-200 object-cover" /> : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold text-slate-600">{((row.first_name || "?")[0] + (row.last_name || "?")[0]).toUpperCase()}</div>}<div className="min-w-0"><p className="whitespace-nowrap font-medium text-slate-900">{((row.first_name || "") + " " + (row.last_name || "")).trim() || "—"}</p><p className="mt-0.5 break-all text-xs text-slate-500">{row.email || "—"}</p></div></div>],
          ["Profession", (row) => row.profession || "—"],
          ["AHPRA", (row) => <div><p className="font-medium text-slate-800">{row.ahpra_number || "—"}</p><p className="mt-0.5 text-xs text-slate-500">{row.verification_status === "VERIFIED" ? "Verified" : "Awaiting verification"}</p>{row.verification_status === "VERIFIED" && <p className={`mt-0.5 text-xs ${row.approval_email_sent_at ? "text-emerald-700" : "font-medium text-amber-700"}`}>{row.approval_email_sent_at ? `Email sent ${fmtDate(row.approval_email_sent_at)}` : "Confirmation email not sent"}</p>}</div>],
          ["Account", (row) => row.accounts?.account_name || "—"],
          ["Signed up", (row) => fmtDate(row.created_at)],
          ["Directory", (row) => row.is_active ? "Listed" : "Not listed"],
        ]}
        onEdit={openEdit}
        empty="No clinicians match these filters."
      />

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader><DialogTitle>Edit clinician</DialogTitle><DialogDescription>Update account details or remove/re-add this profile from the directory without deleting its record.</DialogDescription></DialogHeader>
          <div className="grid max-h-[60vh] grid-cols-1 gap-4 overflow-y-auto pr-1 sm:grid-cols-2">
            {CLINICIAN_FIELDS.map(([key, label]) => (
              <div key={key}><Label htmlFor={"clin-" + key}>{label}</Label><Input id={"clin-" + key} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></div>
            ))}
            <label className="flex items-center gap-2 rounded-md border border-slate-200 p-3 text-sm">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Listed in clinician directory
            </label>
            <label className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <input type="checkbox" checked={!!form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} />
              Test account (hidden from regular users)
            </label>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" className="border-red-200 text-red-700" onClick={remove}><Trash2 className="mr-2 h-4 w-4" /> Permanently delete account</Button>
            <div className="flex flex-wrap gap-2">
              {editing?.verification_status === "VERIFIED" && !editing?.approval_email_sent_at && <Button type="button" variant="outline" onClick={sendApprovalEmail} disabled={sendingApproval}>{sendingApproval && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send AHPRA confirmation</Button>}
              {editing?.verification_status === "VERIFIED" && editing?.approval_email_sent_at && <span className="self-center text-xs font-medium text-emerald-700">Confirmation email sent {fmtDate(editing.approval_email_sent_at)}</span>}
              <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save clinician</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Invite a clinician or clinic</DialogTitle><DialogDescription>Send a secure signup link. Clinician invitations include the current trial and starting-price wording.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Account type</Label><select value={invite.account_type} onChange={(e) => { const value = e.target.value; setInvite({ ...invite, account_type: value, profession: value === "psychiatrist" ? "Psychiatrist" : value === "psychologist" ? "Psychologist" : "" }); }} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3"><option value="psychologist">Psychologist</option><option value="psychiatrist">Psychiatrist</option><option value="clinic">Clinic / practice manager</option></select></div>
            <div><Label>Email address</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
            <div><Label>First name (optional)</Label><Input value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} /></div>
            {invite.account_type === "clinic" && <div><Label>Clinic name (optional)</Label><Input value={invite.clinic_name} onChange={(e) => setInvite({ ...invite, clinic_name: e.target.value })} /></div>}
          </div>
          <Button onClick={sendInvite} disabled={inviting || !invite.email.trim()}>{inviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send invitation</Button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export const AdminGpUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", first_name: "", clinic_name: "" });
  const [inviting, setInviting] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("newest");
  const [accountView, setAccountView] = useState("regular");

  const load = async () => {
    const res = await authedFetch("/admin/gp-users");
    setRows(res.ok ? await res.json() : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const visible = useMemo(() => prepareRows(rows, query, dateRange, sort, accountView), [rows, query, dateRange, sort, accountView]);

  const save = async () => {
    setSaving(true);
    const res = await authedFetch("/admin/gp-users/" + editing.id, { method: "PATCH", body: JSON.stringify(form) });
    setSaving(false);
    if (!res.ok) { toast.error("Could not update GP user."); return; }
    toast.success("GP user updated.");
    setEditing(null);
    load();
  };

  const sendInvite = async () => {
    setInviting(true);
    const res = await authedFetch("/admin/gp-users/invite", { method: "POST", body: JSON.stringify(invite) });
    setInviting(false);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.detail || "Could not send invitation.");
      return;
    }
    toast.success("GP invitation sent through Resend.");
    setInviteOpen(false);
    setInvite({ email: "", first_name: "", clinic_name: "" });
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${editing.first_name} ${editing.last_name}, their sign-in account and GP profile? This cannot be undone.`)) return;
    const res = await authedFetch("/admin/users/gp/" + editing.id, { method: "DELETE" });
    if (!res.ok) return toast.error("Could not delete this GP account.");
    toast.success("GP account permanently deleted.");
    setEditing(null); load();
  };

  return (
    <section data-testid="admin-gp-users-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-slate-900"><Users className="h-5 w-5 text-primary" /> GP users</h2>
          <p className="mt-1 text-sm text-slate-500">GP accounts, signup dates and invitation management.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <EntryTools {...{ searchOpen, setSearchOpen, query, setQuery, dateRange, setDateRange, sort, setSort }}>
            <select value={accountView} onChange={(e) => setAccountView(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium" aria-label="Show regular or test GP accounts">
              <option value="regular">Regular users</option>
              <option value="test">Test accounts</option>
            </select>
          </EntryTools>
          <Button onClick={() => setInviteOpen(true)}><MailPlus className="mr-2 h-4 w-4" /> Invite GP</Button>
        </div>
      </div>
      <UserTable
        loading={loading}
        rows={visible}
        columns={[
          ["GP account", (row) => <div><p className="whitespace-nowrap font-medium text-slate-900">{((row.first_name || "") + " " + (row.last_name || "")).trim() || "—"}</p><p className="mt-0.5 break-all text-xs text-slate-500">{row.email || "—"}</p></div>],
          ["Clinic", (row) => row.clinic_name || "—"],
          ["Signed up", (row) => fmtDate(row.created_at)],
        ]}
        onEdit={(row) => { setEditing(row); setForm({ ...Object.fromEntries(GP_FIELDS.map(([key]) => [key, row[key] || ""])), is_test: !!row.is_test }); }}
        empty="No GP users match these filters."
      />

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle>Edit GP user</DialogTitle><DialogDescription>Update the GP account details shown in their profile.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {GP_FIELDS.map(([key, label]) => (
              <div key={key}><Label htmlFor={"gp-" + key}>{label}</Label><Input id={"gp-" + key} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></div>
            ))}
            <label className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:col-span-2">
              <input type="checkbox" checked={!!form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} />
              Test account (hidden from regular users)
            </label>
          </div>
          <div className="flex flex-wrap justify-between gap-2"><Button variant="outline" className="border-red-200 text-red-700" onClick={remove}><Trash2 className="mr-2 h-4 w-4" /> Permanently delete account</Button><Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save GP user</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Invite a GP</DialogTitle><DialogDescription>Resend will email a secure link to create a SimplyRefer GP account.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="invite-email">Email address</Label><Input id="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
            <div><Label htmlFor="invite-name">First name (optional)</Label><Input id="invite-name" value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} /></div>
            <div><Label htmlFor="invite-clinic">Clinic (optional)</Label><Input id="invite-clinic" value={invite.clinic_name} onChange={(e) => setInvite({ ...invite, clinic_name: e.target.value })} /></div>
          </div>
          <Button onClick={sendInvite} disabled={inviting || !invite.email.trim()}>{inviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send invitation</Button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const UserTable = ({ loading, rows, columns, onEdit, empty }) => (
  <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 bg-white">
    {loading ? (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading users…</div>
    ) : rows.length === 0 ? (
      <div className="p-8 text-center text-sm text-slate-500">{empty}</div>
    ) : (
      <table className="w-full min-w-[1050px] table-auto text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>{columns.map(([label]) => <th key={label} className="px-3 py-3">{label}</th>)}<th className="w-24 px-3 py-3 text-right">Manage</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-50">
              {columns.map(([label, render]) => <td key={label} className="break-words px-3 py-3 align-top text-slate-700">{render(row)}</td>)}
              <td className="w-24 px-3 py-3 text-right align-top"><Button variant="ghost" size="sm" onClick={() => onEdit(row)}><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
